import { InputCheckbox } from '@creditornot/cb-checkbox';
import { DataTable, DataTableProps, RowSelectionConfiguration } from '@creditornot/cb-data-table';
import React from 'react';
import styled from 'styled-components';

import { useIsMobile } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';

type ColumnRenderProps<RecordT, ComponentProps> = ComponentProps & {
  children?: React.ReactNode;
  record: RecordT;
  rowIndex: number;
};

type ResponsiveTableProps<RecordT, ComponentProps, SectionID extends string> = DataTableProps<
  RecordT,
  ComponentProps,
  SectionID
>;

type RowSelectionCheckboxParams<RecordT> = {
  rowSelection: RowSelectionConfiguration<RecordT>;
  getRowKey: (record: RecordT) => string;
  record: RecordT;
  dataSource: RecordT[];
};

const StyledInputCheckbox = styled(InputCheckbox)`
  margin-block-end: 8px;
`;

const RowContainer = styled.div<{ verticalSpacing?: string }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-block: ${({ verticalSpacing = 'spacious' }) => (verticalSpacing === 'spacious' ? '8px' : undefined)};
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const getResponsiveProps = <RecordT, ComponentProps, SectionID extends string>(
  props: ResponsiveTableProps<RecordT, ComponentProps, SectionID>,
  isMobile: boolean,
) =>
  isMobile
    ? {
        ...props,
        rowSelection: props.rowSelection ? { ...props.rowSelection, hideCheckboxColumn: true } : undefined,
        columns: [
          {
            id: 'all-columns',
            name: '',
            style: { display: 'none' }, // this hides the header column and makes the header row collapse
            render: (entryRenderProps: ColumnRenderProps<RecordT, ComponentProps>) => {
              const { columns, dataSource, getRowKey, rowSelection, verticalSpacing } = props;
              return (
                <RowContainer verticalSpacing={verticalSpacing}>
                  {rowSelection && !rowSelection.hideCheckboxColumn && (
                    <RowSelectionCheckbox<RecordT>
                      rowSelection={rowSelection}
                      getRowKey={getRowKey}
                      record={entryRenderProps.record}
                      dataSource={dataSource}
                    />
                  )}
                  {columns.map(column => (
                    <ColumnContainer key={column.id}>
                      <T.Title2>{column.name}</T.Title2>
                      <div>{column.render(entryRenderProps)}</div>
                    </ColumnContainer>
                  ))}
                </RowContainer>
              );
            },
          },
        ],
      }
    : props;

/**
 * This is a copy of DataTable's checkbox implementation, to render it in a single column in mobile view
 */
const RowSelectionCheckbox = <RecordT,>({
  rowSelection: { onChange, selectedRowKeys },
  getRowKey,
  record,
  dataSource,
}: RowSelectionCheckboxParams<RecordT>) => (
  <StyledInputCheckbox
    checked={selectedRowKeys.includes(getRowKey(record))}
    onChange={() => {
      const rowKey = getRowKey(record);
      const newKeys = selectedRowKeys.includes(rowKey)
        ? selectedRowKeys.filter(key => key !== rowKey)
        : [...selectedRowKeys, rowKey];

      const records = dataSource.filter(d => newKeys.includes(getRowKey(d)));

      onChange(newKeys, records);
    }}
    onClick={event => event.stopPropagation()}
  />
);

export const ResponsiveTable = <
  RecordT extends object,
  ComponentProps extends Record<string, unknown> = Record<string, unknown>,
  SectionID extends string = string,
>(
  props: ResponsiveTableProps<RecordT, ComponentProps, SectionID>,
) => {
  const isMobile = useIsMobile();

  return <DataTable {...getResponsiveProps(props, isMobile)} />;
};
